import { BookingRemarksComponent } from '@components/shared/booking-remarks/booking-remarks.component';
import { ErrorStateComponent } from '@components/shared/error-state/error-state.component';
import { ChainMarksComponent } from '@components/shared/chain-marks/chain-marks.component';
import { FlatInfoComponent } from '@components/shared/flat-info/flat-info.component';
import { GuestInfoComponent } from '@components/shared/guest-info/guest-info.component';
import { FlatInfo } from '@models/flat-info.model';
import { TranslateModule } from '@ngx-translate/core';
import { format, parseISO } from 'date-fns';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, input, Input, Output, output } from '@angular/core';
import { GuestInfoDisplayMode } from '@models/guest-info.model';
import { DepartureBookingRow, PresenceStatus } from '../../models/booking-overview.model';
import { createSkeletonRows, ListLoadStatus, SkeletonColumn, SkeletonRowPlaceholder, TableView, tableViewFor } from '../../models/list-load-state';
import { OverviewSkeletonRowComponent } from '../skeleton-row/overview-skeleton-row.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-departures-table',
  standalone: true,
  imports: [CommonModule, TranslateModule, TableModule, ButtonModule, TooltipModule, GuestInfoComponent, FlatInfoComponent, ChainMarksComponent, BookingRemarksComponent, ErrorStateComponent, OverviewSkeletonRowComponent],
  template: `
    <div class="booking-table-container">
      <!-- Load error -->
      @if (view === 'error') {
        <app-error-state
          [title]="'bookingOverview.loadError.title'"
          [message]="'bookingOverview.loadError.message'"
          [retryText]="'common.retry'"
          (onRetry)="retry.emit()"
        />
      }

      <!-- Empty state message -->
      @if (view === 'empty') {
        <div class="p-4 text-center">
          @if (isFilterActive) {
            <p>{{ 'bookingOverview.noFilteredDepartures' | translate }}</p>
            <p class="text-sm text-gray-500">{{ 'bookingOverview.totalDepartures' | translate }}: {{ totalCount }}</p>
          } @else {
            <p>{{ 'bookingOverview.noDepartures' | translate }}</p>
          }
        </div>
      }

      <!-- Table -->
      @if (view === 'rows' || view === 'skeleton') {
        <p-table
          [value]="tableRows"
          styleClass="p-datatable-sm"
          [tableStyle]="{ 'min-width': '60rem' }"
          [scrollable]="true"
          responsiveLayout="scroll"
          [sortField]="sortField"
          [sortOrder]="sortOrder"
          (onSort)="onSort($event)"
        >
          <ng-template pTemplate="header">
            <tr>
              <th
                [pSortableColumn]="'guest.name'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.guest' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'guest.name' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'guest.name' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'guest.name') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th
                [pSortableColumn]="'flatInfo'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.flat' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'flatInfo' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'flatInfo' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'flatInfo') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th style="vertical-align: middle; width: 80px;" class="text-center">
                {{ 'bookingOverview.presence' | translate }}
              </th>
              <th style="vertical-align: middle">{{ 'bookingOverview.entryCode' | translate }}</th>
              <th
                [pSortableColumn]="'checkInDate'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.checkIn' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'checkInDate' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'checkInDate' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'checkInDate') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th
                [pSortableColumn]="'checkOutDate'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.checkOut' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'checkOutDate' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'checkOutDate' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'checkOutDate') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th
                [pSortableColumn]="'isCheckedIn'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.checkedIn' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'isCheckedIn' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'isCheckedIn' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'isCheckedIn') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th style="vertical-align: middle">{{ 'bookingOverview.checkOutStatus' | translate }}</th>
              <th style="vertical-align: middle; width: 60px;"></th>
            </tr>
          </ng-template>
          <ng-template
            pTemplate="body"
            let-booking
          >
            @if (view === 'skeleton') {
              <tr appOverviewSkeletonRow [columns]="skeletonColumns"></tr>
            } @else {
              <tr class="animate-fadein animate-duration-200 animate-ease-out">
                <td>
                  <div
                    class="cursor-pointer"
                    (click)="onGuestInfoClick(booking)"
                    pTooltip="{{ 'common.viewDetails' | translate }}"
                    tooltipPosition="top"
                    [tooltipDisabled]="remarks.isOpen()"
                  >
                    <app-guest-info [guestInfo]="booking.guest" [displayMode]="displayMode">
                      <app-booking-remarks #remarks guestNameSuffix [remarks]="booking.remarks" />
                    </app-guest-info>
                  </div>
                  <div class="mt-1 flex flex-wrap gap-1"><app-chain-marks [marks]="booking.chain"></app-chain-marks></div>
                </td>
                <td>
                  @if (booking.flatInfos.length > 0) {
                    @for (flat of booking.flatInfos; track flat; let i = $index) {
                      <app-flat-info [flatInfo]="flat"></app-flat-info>
                      @if (i < booking.flatInfos.length - 1) {
                        <br />
                      }
                    }
                  } @else {
                    {{ 'common.unknown' | translate }}
                  }
                </td>
                <td class="text-center">
                  @if (booking.presenceLoading) {
                    <i class="pi pi-spin pi-spinner text-sm"></i>
                  }
                  @if (!booking.presenceLoading && booking.presenceStatus && booking.presenceStatus.isOccupied) {
                    <div 
                      class="cursor-pointer inline-flex items-center justify-center"
                      (click)="onPresenceClick(booking)"
                      [pTooltip]="getPresenceTooltip(booking)"
                      tooltipPosition="top">
                      <div class="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
                        <i class="pi pi-check text-sm text-green-600"></i>
                      </div>
                    </div>
                  }
                  @if (!booking.presenceLoading && booking.presenceStatus && !booking.presenceStatus.isOccupied) {
                    <div 
                      class="cursor-pointer inline-flex items-center justify-center"
                      (click)="onPresenceClick(booking)"
                      [pTooltip]="getPresenceTooltip(booking)"
                      tooltipPosition="top">
                      <div class="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100">
                        <i class="pi pi-minus text-sm text-gray-400"></i>
                      </div>
                    </div>
                  }
                  @if (!booking.presenceLoading && !booking.presenceStatus) {
                    <span class="text-gray-400 text-sm">—</span>
                  }
                </td>
                <td>
                  <!-- Loading state -->
                  @if (booking.entryCodesLoading) {
                    <div class="flex items-center justify-center">
                      <i
                        class="pi pi-spin pi-spinner"
                        style="font-size: 0.75rem"
                      ></i>
                    </div>
                  }
                  <!-- Entry codes loaded -->
                  @if (!booking.entryCodesLoading && booking.entryCodes.length > 0) {
                    <div class="text-center">
                      @for (code of booking.entryCodes; track $index) {
                        <div>
                          <span class="font-bold">{{ code }}</span>
                          @if (booking.flatInfos.length > 1 && $index < booking.entryCodes.length - 1) {
                            <br />
                          }
                        </div>
                      }
                      @if (booking.entryCodesFailed) {
                        <i
                          class="pi pi-exclamation-triangle text-sm text-orange-500 dark:text-orange-400"
                          role="img"
                          [attr.aria-label]="'bookingOverview.entryCodeLoadFailed' | translate"
                          [pTooltip]="'bookingOverview.entryCodeLoadFailed' | translate"
                          tooltipPosition="top"
                        ></i>
                      }
                    </div>
                  }
                  <!-- No entry codes available -->
                  @if (!booking.entryCodesLoading && booking.entryCodes.length === 0) {
                    <div class="text-center">
                      @if (booking.entryCodesFailed) {
                        <i
                          class="pi pi-exclamation-triangle text-sm text-orange-500 dark:text-orange-400"
                          role="img"
                          [attr.aria-label]="'bookingOverview.entryCodeLoadFailed' | translate"
                          [pTooltip]="'bookingOverview.entryCodeLoadFailed' | translate"
                          tooltipPosition="top"
                        ></i>
                      } @else {
                        <span class="text-sm text-gray-500">—</span>
                      }
                    </div>
                  }
                </td>
                <td>{{ formatDate(booking.checkInDate) }}</td>
                <td>{{ formatDate(booking.checkOutDate) }}</td>
                <td class="text-center">
                  <div class="flex justify-center">
                    @if (booking.isCheckedIn) {
                      <div class="flex items-center justify-center rounded-full bg-green-100 p-1 text-green-700">
                        <i class="pi pi-check !text-sm font-bold !leading-none"></i>
                      </div>
                    } @else {
                      <span class="text-lg text-red-300">—</span>
                    }
                  </div>
                </td>
                <td>
                  <div class="flex items-center justify-between">
                    <div class="flex gap-2">
                      <button
                        pButton
                        icon="pi pi-comments"
                        class="p-button-outlined p-button-sm rounded-md"
                        [disabled]="!!booking.entryCodesFailed"
                        pTooltip="{{ (booking.entryCodesFailed ? 'bookingOverview.communicationUnavailableNoEntryCode' : 'communication.title') | translate }}"
                        tooltipPosition="top"
                        (click)="onCommunicationClick(booking)"
                      ></button>
                      @if (booking.isCheckedIn) {
                        <button
                          pButton
                          icon="pi pi-copy"
                          class="p-button-outlined p-button-sm p-button-success rounded-md"
                          pTooltip="{{ 'bookingOverview.copyCheckin' | translate }}"
                          tooltipPosition="top"
                          (click)="onCopyCheckinClick(booking)"
                        ></button>
                      }
                      @if (!booking.isCheckedIn && hasCheckinInClipboard) {
                        <button
                          pButton
                          icon="pi pi-clipboard"
                          class="p-button-outlined p-button-sm p-button-warning rounded-md"
                          pTooltip="{{ 'bookingOverview.pasteCheckin' | translate }}"
                          tooltipPosition="top"
                          (click)="onPasteCheckinClick(booking)"
                        ></button>
                      }
                    </div>
                    <div>
                      @if (booking.checkOutDateTime) {
                        <!-- User has checked out -->
                        <div class="flex flex-col gap-1 ml-2">
                          <div class="flex justify-end text-green-500 mb-1">
                            <i class="pi pi-check-circle mr-2"></i>
                            <span>{{ formatTime(booking.checkOutDateTime) }}</span>
                          </div>
                        </div>
                        <button
                          pButton
                          label="{{ 'bookingOverview.cancelCheckOut' | translate }}"
                          icon="pi pi-times"
                          class="p-button-outlined p-button-danger p-button-sm rounded-md ml-2"
                          (click)="onCancelCheckOutClick(booking)"
                        ></button>
                      } @else {
                        <!-- User has not checked out yet -->
                        <button
                          pButton
                          label="{{ 'bookingOverview.checkOut' | translate }}"
                          icon="pi pi-sign-out"
                          class="p-button-outlined p-button-primary p-button-sm rounded-md"
                          (click)="onCheckOutClick(booking)"
                        ></button>
                      }
                    </div>
                  </div>
                </td>
                <td class="text-center">
                  <button
                    pButton
                    type="button"
                    icon="pi pi-link"
                    class="p-button-rounded p-button-text p-button-sm"
                    [disabled]="!!booking.chain?.isExtended"
                    [pTooltip]="booking.chain?.isExtended ? ('booking.chain.linkOnlyFromTail' | translate) : ('booking.chain.link' | translate)"
                    tooltipPosition="left"
                    (click)="linkChain.emit(booking.id); $event.stopPropagation()"
                  ></button>
                </td>
              </tr>
            }
          </ng-template>
        </p-table>
      }
    </div>
  `
})
export class DeparturesTableComponent {
  @Input() departures: DepartureBookingRow[] = [];
  @Input() isFilterActive = false;
  @Input() totalCount = 0;
  @Input() displayMode: GuestInfoDisplayMode = GuestInfoDisplayMode.USER;

  @Output() guestInfoClicked = new EventEmitter<number>();
  @Output() checkOut = new EventEmitter<number>();
  @Output() cancelCheckOut = new EventEmitter<number>();
  @Output() presenceClicked = new EventEmitter<{ booking: DepartureBookingRow; presenceStatus: PresenceStatus | undefined }>();
  @Output() communicationClicked = new EventEmitter<DepartureBookingRow>();
  @Output() copyCheckin = new EventEmitter<DepartureBookingRow>();
  @Output() pasteCheckin = new EventEmitter<DepartureBookingRow>();
  readonly linkChain = output<number>();

  @Input() hasCheckinInClipboard: boolean = false;

  readonly loadStatus = input<ListLoadStatus>('ready');
  readonly retry = output<void>();

  protected readonly skeletonRows = createSkeletonRows();
  protected readonly skeletonColumns: readonly SkeletonColumn[] = ['guest', 'flat', 'icon', 'text', 'text', 'text', 'icon', 'text', 'actions'];

  /** Töltés közben helykitöltő, hibánál hibaállapot, különben a sorok vagy az üres állapot. */
  protected get view(): TableView {
    return tableViewFor(this.loadStatus(), this.departures?.length ?? 0);
  }

  /** A p-table sorai: töltés közben a helykitöltők, különben a kapott sorok. */
  protected get tableRows(): (DepartureBookingRow | SkeletonRowPlaceholder)[] {
    return this.view === 'skeleton' ? this.skeletonRows : this.departures;
  }

  private readonly translateService = inject(TranslateService);

  sortField: string = '';
  sortOrder: number = 1;
  sortState: { field: string | null; direction: 'asc' | 'desc' | null } = {
    field: null,
    direction: null
  };

  /**
   * Handle check out button click
   * @param booking Booking data
   */
  onCheckOutClick(booking: DepartureBookingRow): void {
    if (booking.id) {
      this.checkOut.emit(booking.id);
      // The parent component will update the booking list after successful checkout
    }
  }

  /**
   * Handle cancel check out button click
   * @param booking Booking data
   */
  onCancelCheckOutClick(booking: DepartureBookingRow): void {
    if (booking.id) {
      this.cancelCheckOut.emit(booking.id);
      // The parent component will update the booking list after successful cancellation
    }
  }

  /**
   * Handle guest name click
   * @param booking Booking data
   */
  onGuestInfoClick(booking: DepartureBookingRow): void {
    if (booking.id) {
      this.guestInfoClicked.emit(booking.id);
    }
  }

  /**
   * Handle presence click
   * @param booking Booking data
   */
  onPresenceClick(booking: DepartureBookingRow): void {
    this.presenceClicked.emit({
      booking,
      presenceStatus: booking.presenceStatus
    });
  }

  /**
   * Handle communication button click
   * @param booking Booking data
   */
  onCommunicationClick(booking: DepartureBookingRow): void {
    if (booking.entryCodesFailed) {
      return;
    }
    this.communicationClicked.emit(booking);
  }

  onCopyCheckinClick(booking: DepartureBookingRow): void {
    this.copyCheckin.emit(booking);
  }

  onPasteCheckinClick(booking: DepartureBookingRow): void {
    this.pasteCheckin.emit(booking);
  }

  /**
   * Get presence tooltip text
   * @param booking Booking data
   * @returns Presence tooltip text
   */
  getPresenceTooltip(booking: DepartureBookingRow): string {
    if (!booking.presenceStatus) return '';
    
    const status = booking.presenceStatus.isOccupied 
      ? this.translateService.instant('presence.occupied')
      : this.translateService.instant('presence.empty');
    
    return `${status} • ${this.getTimeSince(booking.presenceStatus.minutesSinceChange)}`;
  }

  /**
   * Get time since presence status change
   * @param minutes Minutes since presence status change
   * @returns Time since presence status change text
   */
  private getTimeSince(minutes: number): string {
    if (minutes < 1) return 'kevesebb mint 1 perce';
    if (minutes < 60) return `${Math.floor(minutes)} perce`;
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 órája';
    if (hours < 24) return `${hours} órája`;
    const days = Math.floor(hours / 24);
    return `${days} napja`;
  }

  /**
   * Format date for display in table
   * @param date Date object or string to format
   * @returns Formatted date string
   */
  formatDate(date: Date | string): string {
    if (!date) return '';
    try {
      if (typeof date === 'string') {
        return format(new Date(date), 'yyyy-MM-dd');
      }
      return format(date, 'yyyy-MM-dd');
    } catch (e) {
      return '';
    }
  }

  /**
   * Format time only from a datetime
   * @param dateTime Date/time string to format
   * @returns Formatted time string (HH:mm)
   */
  formatTime(dateTime: string): string {
    try {
      const parsed = parseISO(dateTime);
      return format(parsed, 'HH:mm');
    } catch (e) {
      return '';
    }
  }

  /**
   * Format checkout date/time for display
   * @param dateTime Date/time string to format
   * @returns Formatted date/time string
   */
  formatCheckOutDateTime(dateTime: string): string {
    try {
      const parsed = parseISO(dateTime);
      return format(parsed, 'yyyy-MM-dd HH:mm');
    } catch (e) {
      return dateTime || '';
    }
  }

  /**
   * Handle sort event from p-table
   * @param event Sort event data
   */
  onSort(event: any): void {
    this.sortField = event.field;
    this.sortOrder = event.order;

    // Update sort state for custom icons
    this.sortState.field = event.field;
    this.sortState.direction = event.order === 1 ? 'asc' : 'desc';

    if (this.sortField === 'flatInfo') {
      this.departures = [...this.departures].sort((a, b) => {
        const result = FlatInfo.compare(a.flatInfos[0], b.flatInfos[0]);
        return this.sortOrder === 1 ? result : -result;
      });
    }
  }
}
